import { authenticatedBitswanBackendInstance } from "@/lib/api-client";
import { ApiResponse } from "./shared";
import { getActiveOrgFromCookies } from "./organisations";

export type Workspace = {
  id: string;
  name: string;
  keycloak_org_id: string;
  workspace_group_id?: string | null;
  automation_server: string;
  automation_server_name: string;
  created_at: string;
  updated_at: string;
  editor_url: string | null;
};

export type WorkspaceGroup = {
  id: string;
  name: string;
  path: string;
  tag_color: string | null;
  permissions: string[];
  description: string | null;
};

export type WorkspaceGroupUser = {
  id: string;
  username: string;
  email: string;
  emailVerified?: boolean;
  firstName?: string;
  lastName?: string;
};

export const getWorkspaces = async (
  automationServerId?: string
): Promise<Workspace[]> => {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();
  const activeOrg = await getActiveOrgFromCookies();

  try {
    const params: Record<string, string> = {};
    if (automationServerId) {
      params.automation_server_id = automationServerId;
    }
    const res = await bitswanBEInstance.get<Workspace[]>(
      `/workspaces/`,
      {
        params,
        headers: {
          "X-Org-Id": activeOrg?.id ?? "",
        },
      },
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching workspaces", error);
    return [];
  }
};


export const getWorkspaceById = async (workspaceId: string): Promise<Workspace | null> => {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();
  const activeOrg = await getActiveOrgFromCookies();

  try {
    const res = await bitswanBEInstance.get<Workspace>(
    `/workspaces/${workspaceId}/`,
    {
      headers: {
        "X-Org-Id": activeOrg?.id ?? "",
      },
    },
  );
    return res.data;
  } catch (error) {
    console.error("Error fetching workspace", error);
    return null
  }
};

export const getWorkspaceGroups = async (workspaceId: string): Promise<WorkspaceGroup[]> => {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();
  const activeOrg = await getActiveOrgFromCookies();

  try {
    const res = await bitswanBEInstance.get<WorkspaceGroup[]>(
      `/workspaces/${workspaceId}/groups/`,
      {
        headers: {
          "X-Org-Id": activeOrg?.id ?? "",
        },
      },
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching workspace groups", error);
    return [];
  }
};

export const getWorkspaceUsers = async (
  workspaceId: string
): Promise<WorkspaceGroupUser[]> => {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();
  const activeOrg = await getActiveOrgFromCookies();

  try {
    const res = await bitswanBEInstance.get<WorkspaceGroupUser[]>(
      `/workspaces/${workspaceId}/users/`,
      {
        headers: {
          "X-Org-Id": activeOrg?.id ?? "",
        },
      },
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching workspace users", error);
    return [];
  }
};

export const getWorkspaceNonMemberGroups = async (
  workspaceId: string
): Promise<WorkspaceGroup[]> => {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();
  const activeOrg = await getActiveOrgFromCookies();

  try {
    const res = await bitswanBEInstance.get<WorkspaceGroup[]>(
      `/workspaces/${workspaceId}/non-member-groups/`,
      {
        headers: {
          "X-Org-Id": activeOrg?.id ?? "",
        },
      },
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching workspace non-member groups", error);
    return [];
  }
};

export const getWorkspaceNonMemberUsers = async (
  workspaceId: string
): Promise<WorkspaceGroupUser[]> => {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();
  const activeOrg = await getActiveOrgFromCookies();

  try {
    const res = await bitswanBEInstance.get<WorkspaceGroupUser[]>(
      `/workspaces/${workspaceId}/non-member-users/`,
      {
        headers: {
          "X-Org-Id": activeOrg?.id ?? "",
        },
      },
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching workspace non-member users", error);
    return [];
  }
};

export const addUserToWorkspace = async (
  workspaceId: string,
  userId: string
): Promise<ApiResponse<null>> => {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();
  const activeOrg = await getActiveOrgFromCookies();

  try {
    const res = await bitswanBEInstance.post<ApiResponse>(
      `/workspaces/${workspaceId}/add_user/`,
      {
        user_id: userId,
      },
      {
        headers: {
          "X-Org-Id": activeOrg?.id ?? "",
        },
      },
    );
    return { ...res.data, status: "success" as const, data: null };
  } catch (error) {
    console.error("Error adding user to workspace", error);
    return {
      status: "error" as const,
      message: "Error adding user to workspace",
      data: null,
    };
  }
};

export const removeUserFromWorkspace = async (
  workspaceId: string,
  userId: string
): Promise<ApiResponse<null>> => {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();
  const activeOrg = await getActiveOrgFromCookies();

  try {
    const res = await bitswanBEInstance.post<ApiResponse>(
      `/workspaces/${workspaceId}/remove_user/`,
      {
        user_id: userId,
      },
      {
        headers: {
          "X-Org-Id": activeOrg?.id ?? "",
        },
      },
    );
    return { ...res.data, status: "success" as const, data: null };
  } catch (error) {
    console.error("Error removing user from workspace", error);
    return {
      status: "error" as const,
      message: "Error removing user from workspace",
      data: null,
    };
  }
};
